import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import { Menu, Layout } from 'antd'
import classNames from 'classnames'
import store from 'store'
import PerfectScrollbar from 'react-perfect-scrollbar'
import BRAND from 'brand'
import style from './style.module.scss'

const mapStateToProps = ({ menu, settings, user }) => ({
  menuData: menu.menuData,
  isMenuCollapsed: settings.isMenuCollapsed,
  isMobileView: settings.isMobileView,
  isMenuUnfixed: settings.isMenuUnfixed,
  isMenuShadow: settings.isMenuShadow,
  leftMenuWidth: settings.leftMenuWidth,
  menuColor: settings.menuColor,
  logo: settings.logo,
  role: user.role,
  businessModels: user.businessModels,
  modules: user.modules,
})

function MenuLeft({
  dispatch,
  menuData = [],
  location: { pathname },
  isMenuCollapsed,
  isMobileView,
  isMenuUnfixed,
  isMenuShadow,
  leftMenuWidth,
  menuColor,
  role,
  businessModels,
  modules,
}) {
  const [selectedKeys, setSelectedKeys] = useState(store.get('app.menu.selectedKeys') || [])
  const [openedKeys, setOpenedKeys] = useState(store.get('app.menu.openedKeys') || [])

  useEffect(() => {
    applySelectedKeys()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menuData])

  const applySelectedKeys = () => {
    const flattenItems = (items, key) =>
      items.reduce((flattenedItems, item) => {
        flattenedItems.push(item)
        if (Array.isArray(item[key])) {
          return flattenedItems.concat(flattenItems(item[key], key))
        }
        return flattenedItems
      }, [])
    let selectedItem = []
    const items = flattenItems(menuData, 'children')
    items.forEach(i => {
      if (i.url && pathname.startsWith(i.url)) {
        selectedItem = i
      }
    })
    setSelectedKeys(selectedItem ? [selectedItem.key] : [])
  }

  const onCollapse = (value, type) => {
    if (type === 'responsive' && isMenuCollapsed) {
      return
    }
    dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'isMenuCollapsed',
        value: !isMenuCollapsed,
      },
    })
    setOpenedKeys([])
  }

  const onOpenChange = keys => {
    store.set('app.menu.openedKeys', keys)
    setOpenedKeys(keys)
  }

  const handleClick = e => {
    const selectedItem = menuData
      .flatMap(item => (item.children ? [item, ...item.children] : item))
      .find(item => item.key === e.key)

    if (selectedItem && selectedItem.url) {
      dispatch({
        type: 'router/REDIRECT',
        payload: {
          path: selectedItem.url,
        },
      })
    }

    store.set('app.menu.selectedKeys', [e.key])
    setSelectedKeys([e.key])
  }

  const generateMenuItems = () => {
    const generateItem = item => {
      const { key, title, url, icon, disabled, count, children, category, roles } = item
      if (roles && !roles.includes(role)) {
        return null
      }
      if (category) {
        return {
          key: `group-${title}`,
          label: title,
          type: 'group',
        }
      }
      if (item.businessModels && !item.businessModels.some(bm => businessModels.includes(bm))) {
        return null
      }
      if (item.modules && !item.modules.some(m => modules.includes(m))) {
        return null
      }
      // The icons are absolutely positioned to the right by the stylesheet, so
      // they have to live inside the item content. Passing them to antd's
      // `icon` slot instead reserves space on the left, which indents the label
      // away from the section titles and breaks the collapsed layout.
      if (children) {
        return {
          key,
          label: (
            <span>
              <span className={style.title}>{title}</span>
              {count && <span className="badge badge-success ml-2">{count}</span>}
              {icon && <span className={`${icon} ${style.icon}`} />}
            </span>
          ),
          children: children.map(generateItem),
        }
      }
      return {
        key,
        label: url ? (
          <Link to={url}>
            <span className={style.title}>{title}</span>
            {count && <span className="badge badge-success ml-2">{count}</span>}
            {icon && <span className={`${icon} ${style.icon} icon-collapsed-hidden`} />}
          </Link>
        ) : (
          <span>
            <span className={style.title}>{title}</span>
            {count && <span className="badge badge-success ml-2">{count}</span>}
            {icon && <span className={`${icon} ${style.icon} icon-collapsed-hidden`} />}
          </span>
        ),
        disabled,
      }
    }
    return menuData.map(generateItem).filter(Boolean)
  }

  const menuSettings = isMobileView
    ? {
        width: leftMenuWidth,
        collapsible: false,
        collapsed: false,
        onCollapse,
      }
    : {
        width: leftMenuWidth,
        collapsible: true,
        collapsed: isMenuCollapsed,
        onCollapse,
        breakpoint: 'lg',
      }

  return (
    <Layout.Sider
      {...menuSettings}
      className={classNames(`${style.menu}`, {
        [style.white]: menuColor === 'white',
        [style.gray]: menuColor === 'gray',
        [style.dark]: menuColor === 'dark',
        [style.unfixed]: isMenuUnfixed,
        [style.shadow]: isMenuShadow,
      })}
    >
      <div
        className={style.menuOuter}
        style={{
          width: isMenuCollapsed && !isMobileView ? 80 : leftMenuWidth,
          height: isMobileView || isMenuUnfixed ? 'calc(100% - 64px)' : 'calc(100% - 110px)',
        }}
      >
        <div className={style.logoContainer}>
          <div className={style.logo}>
            <img src="resources/images/logo.svg" className="mr-0" width="64" alt={BRAND.name} />
          </div>
        </div>
        <PerfectScrollbar>
          <Menu
            onClick={handleClick}
            selectedKeys={selectedKeys}
            openKeys={openedKeys}
            onOpenChange={onOpenChange}
            mode="inline"
            className={style.navigation}
            inlineIndent={15}
            items={generateMenuItems()}
          />
          {/* <div className={style.banner}>
            <p>More components, more style, more themes, and premium support!</p>
            <a
              href="https://themeforest.net/item/clean-ui-react-admin-template/21938700"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-success btn-rounded px-3"
            >
              Buy Bundle
            </a>
          </div> */}
        </PerfectScrollbar>
      </div>
    </Layout.Sider>
  )
}

export default withRouter(connect(mapStateToProps)(MenuLeft))
